import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";

// ---------- Shared in-memory cache ----------
// Keyed by endpoint + serialised params. Returns a Promise so concurrent
// callers de-dupe onto the same network request. TTL keeps data fresh
// without hammering the API when the user hops between pages.
const _mem = new Map();

export function memGet(key) {
  const hit = _mem.get(key);
  if (!hit) return null;
  if (hit.expiresAt < Date.now()) { _mem.delete(key); return null; }
  return hit.value;
}
export function memSet(key, value, ttlMs = 60_000) {
  _mem.set(key, { value, expiresAt: Date.now() + ttlMs });
}
export function memInvalidate(prefix) {
  for (const k of _mem.keys()) if (k.startsWith(prefix)) _mem.delete(k);
}

/**
 * useCachedGet — GET with in-memory cache + de-duplication.
 * Returns { data, loading, error, reload, setData }.
 * `ttlMs` defaults to 60s. Pass `swr:true` to return cached data instantly
 * AND revalidate in the background (stale-while-revalidate).
 */
export function useCachedGet(url, { params, ttlMs = 60_000, swr = true, enabled = true, deps = [] } = {}) {
  const key = url + "?" + JSON.stringify(params || {});
  const cached = memGet(key);
  // Return `undefined` (not `null`) before first fetch so callers can rely on
  // `const { data = [] } = useCachedGet(...)` destructuring defaults.
  const [data, setData] = useState(cached === undefined || cached === null ? undefined : cached);
  const [loading, setLoading] = useState(cached === undefined || cached === null ? enabled : false);
  const [error, setError] = useState(null);

  const inflight = useRef(null);

  const fetchOnce = async () => {
    if (!enabled) return;
    if (inflight.current) return inflight.current;
    inflight.current = (async () => {
      try {
        const res = await api.get(url, { params });
        memSet(key, res.data, ttlMs);
        setData(res.data);
        setError(null);
        return res.data;
      } catch (e) {
        setError(e);
        throw e;
      } finally {
        setLoading(false);
        inflight.current = null;
      }
    })();
    return inflight.current;
  };

  useEffect(() => {
    if (!enabled) return;
    if (cached && swr) {
      // Serve cached instantly; revalidate in background silently.
      setData(cached); setLoading(false);
      fetchOnce();
    } else {
      setLoading(true);
      fetchOnce();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, enabled, ...deps]);

  return { data, loading, error, reload: fetchOnce, setData };
}

// ---------- Debounced value ----------
export function useDebounced(value, ms = 250) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}
